import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Eye, EyeOff, FileSearch, Paperclip, Pencil, Send, Sparkles, Trash2 } from 'lucide-react';
import { apiService } from '../../services/api';
import ExpenseForm from '../ExpenseManagement/ExpenseForm';
import LineAllocations, { CloseButton, ProjectTotals } from './LineAllocations';
import NexusInsights from './NexusInsights';
import { fileHref, fullDate, money, Pill, ReceiptLinks, Spinner } from './ui';

// Détail d'une facture enregistrée : justificatif, lignes et répartition (modifiable à la main ou par une consigne),
// formulaire complet (ancien) et suppression.

const STATUS_NOTE = {
  approx: 'Facture ancienne sur plusieurs projets : les lignes sont réparties au prorata (à préciser si besoin).',
  failed: 'Les lignes de cette facture n’ont pas pu être relevées automatiquement.',
  skipped: 'Pas de justificatif lisible pour relever les lignes.',
};

const InvoiceDrawer = ({ invoice, projects, loadLegacyProjects, employeeId, onClose, onChanged, onDeleted }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [showPdf, setShowPdf] = useState(false);
  const [instruction, setInstruction] = useState('');
  const [preview, setPreview] = useState(null);
  const [legacy, setLegacy] = useState(null);
  const currency = invoice.currency || '€';

  const load = useCallback(() => {
    setLoading(true);
    return apiService.get(`/expenses/${invoice.id}/lines`).then(setDetail).catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [invoice.id]);

  useEffect(() => { setPreview(null); setInstruction(''); setError(null); load(); }, [load]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !legacy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, legacy]);

  const lines = useMemo(() => (detail?.lines || []).map((l) => ({ ...l, ref: l.reference, mfr_ref: l.mfr_reference })), [detail]);
  const idOf = useMemo(() => Object.fromEntries(lines.map((l) => [l.position, l.id])), [lines]);
  const allocations = useMemo(() => lines.flatMap((l) => l.allocations.map((a) => ({ ...a, line: l.position }))), [lines]);
  const names = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p.name])), [projects]);

  const run = async (kind, action) => {
    setBusy(kind);
    setError(null);
    try {
      return await action();
    } catch (err) {
      setError(err.message || 'Erreur');
      return null;
    } finally {
      setBusy(null);
    }
  };

  const applyAllocations = (list) => run('split', async () => {
    const result = await apiService.post(`/expenses/${invoice.id}/split`, {
      allocations: list.map((a) => ({ line_id: idOf[a.line], project_id: a.project_id, iteration_id: a.iteration_id || null, quantity: a.quantity })),
    });
    if (!result.applied) throw new Error('Toutes les lignes doivent être affectées.');
    setDetail(result);
    setPreview(null);
    onChanged();
  });

  const propose = () => run('propose', async () => {
    setPreview(await apiService.post(`/expenses/${invoice.id}/split`, { instruction, dry_run: true }));
  });

  const decompose = () => run('decompose', async () => {
    setDetail(await apiService.post(`/expenses/${invoice.id}/decompose`, {}));
    onChanged();
  });

  const remove = () => {
    if (!window.confirm('Supprimer cette dépense ? Ses lignes et sa répartition seront supprimées.')) return;
    run('delete', async () => {
      await apiService.delete(`/expenses/${invoice.id}`);
      onDeleted(invoice.id);
    });
  };

  const openLegacy = () => run('legacy', async () => {
    const [expense, legacyProjects] = await Promise.all([apiService.get(`/expenses/${invoice.id}`), loadLegacyProjects()]);
    setLegacy({ expense, projects: legacyProjects });
  });

  const saveLegacy = async (data, expenseId) => {
    if (data instanceof FormData) await apiService.uploadFile(`/expenses/${expenseId}`, data, null, 'PATCH');
    else await apiService.patch(`/expenses/${expenseId}`, data);
    setLegacy(null);
    load();
    onChanged();
  };

  const previewProjects = useMemo(() => {
    if (!preview?.rows) return [];
    const totals = {};
    preview.rows.forEach((r) => {
      totals[r.project_id] = totals[r.project_id] || { project_id: r.project_id, project_name: names[r.project_id], amount: 0 };
      totals[r.project_id].amount += r.amount;
    });
    return Object.values(totals);
  }, [preview, names]);

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <button type="button" aria-label="Fermer" onClick={onClose} className="absolute inset-0 bg-gray-900/20" />
      <aside className={`relative flex h-full w-full flex-col bg-white shadow-2xl transition-all ${showPdf ? 'max-w-5xl' : 'max-w-xl'}`}>
        <header className="flex items-start gap-3 border-b border-gray-200 px-5 py-4">
          <div className="min-w-0 flex-1">
            <p className="text-xs text-gray-500">{fullDate(invoice.date)}{invoice.invoice_number && ` · n° ${invoice.invoice_number}`}</p>
            <h2 className="truncate text-lg font-semibold text-gray-900">{invoice.vendor || 'Fournisseur inconnu'}</h2>
            <p className="text-sm text-gray-600">
              <span className="font-semibold tabular-nums text-gray-900">{money(invoice.amount, currency)}</span> HT
              {invoice.tva != null && <span className="text-gray-500"> · TVA {money(invoice.tva, currency)} · {money((Number(invoice.amount) || 0) + (Number(invoice.tva) || 0), currency)} TTC</span>}
            </p>
            <p className="mt-0.5 text-xs text-gray-500">{invoice.employee_name && `Saisie par ${invoice.employee_name}`}</p>
          </div>
          <div className="flex items-center gap-0.5">
            <ReceiptLinks receipts={invoice.receipts} />
            {invoice.file_url && (
              <>
                <button type="button" onClick={() => setShowPdf((v) => !v)} title={showPdf ? 'Masquer le justificatif' : 'Voir le justificatif'}
                  className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
                  {showPdf ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
                <a href={fileHref(invoice.file_url)} target="_blank" rel="noopener noreferrer" title="Ouvrir dans un onglet"
                  className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"><Paperclip className="h-4 w-4" /></a>
              </>
            )}
            <button type="button" onClick={openLegacy} title="Formulaire complet" className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
              {busy === 'legacy' ? <Spinner /> : <Pencil className="h-4 w-4" />}
            </button>
            <button type="button" onClick={remove} title="Supprimer" className="rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600">
              <Trash2 className="h-4 w-4" />
            </button>
            <CloseButton onClick={onClose} />
          </div>
        </header>

        <div className={`min-h-0 flex-1 ${showPdf ? 'grid lg:grid-cols-2' : ''}`}>
          {showPdf && <iframe title="Justificatif" src={fileHref(invoice.file_url)} className="h-full min-h-[24rem] w-full border-r border-gray-100 bg-gray-50" />}
          <div className="h-full space-y-4 overflow-y-auto px-5 py-4">
            {loading && <Spinner />}
            {!loading && detail && (detail.lines.length ? (
              <>
                {detail.projects.length > 0 && <ProjectTotals projects={detail.projects} currency={currency} />}
                {STATUS_NOTE[detail.lines_status] && <Pill tone="amber">{STATUS_NOTE[detail.lines_status]}</Pill>}
                <LineAllocations lines={lines} allocations={allocations} currency={currency} projects={projects}
                  editable saving={busy === 'split'} onChange={applyAllocations} />
                <NexusInsights expenseId={invoice.id}
                  recent={Boolean(invoice.created_at) && Date.now() - new Date(invoice.created_at).getTime() < 10 * 60 * 1000} />
                <div className="rounded-lg border border-violet-100 bg-violet-50/40 p-3">
                  <div className="flex items-start gap-2">
                    <Sparkles className="mt-2 h-4 w-4 shrink-0 text-violet-500" />
                    <textarea rows={1} value={instruction} onChange={(e) => setInstruction(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && instruction.trim()) { e.preventDefault(); propose(); } }}
                      placeholder="Nouvelle répartition, ex. « la moitié des LX sur Olista 30 »"
                      className="min-h-[2.25rem] flex-1 resize-y rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm focus:border-violet-400 focus:outline-none" />
                    <button type="button" onClick={propose} disabled={!instruction.trim() || busy === 'propose'}
                      className="mt-0.5 inline-flex items-center gap-1 rounded-lg bg-violet-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-40">
                      {busy === 'propose' ? <Spinner className="h-4 w-4 text-white" /> : <Send className="h-4 w-4" />} Proposer
                    </button>
                  </div>
                  {preview && (
                    <div className="mt-3 space-y-2">
                      {(preview.questions || []).map((q) => <p key={q} className="text-xs text-violet-900">{q}</p>)}
                      <ProjectTotals projects={previewProjects} currency={currency} />
                      {Object.keys(preview.unallocated || {}).length > 0 && (
                        <p className="text-xs text-amber-800">Des lignes restent sans projet : précise la consigne ou complète à la main.</p>
                      )}
                      <div className="flex justify-end gap-2">
                        <button type="button" onClick={() => setPreview(null)} className="rounded-md px-2.5 py-1 text-xs text-gray-600 hover:bg-white">Annuler</button>
                        <button type="button" disabled={Object.keys(preview.unallocated || {}).length > 0 || busy === 'split'}
                          onClick={() => applyAllocations(preview.rows.map((r) => ({ line: r.line, project_id: r.project_id, iteration_id: r.iteration_id, quantity: r.quantity })))}
                          className="rounded-md bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-40">
                          Appliquer
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-600">
                <p>{STATUS_NOTE[detail.lines_status] || 'Les lignes de cette facture n’ont pas encore été relevées (Nexus s’en occupe en tâche de fond).'}</p>
                {invoice.file_url && (
                  <button type="button" onClick={decompose} disabled={busy === 'decompose'}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
                    {busy === 'decompose' ? <Spinner className="h-4 w-4 text-white" /> : <FileSearch className="h-4 w-4" />} Relever les lignes maintenant
                  </button>
                )}
              </div>
            ))}
            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>
        </div>
      </aside>

      {legacy && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-gray-900/40 p-4">
          <div className="w-full max-w-4xl rounded-xl bg-white shadow-2xl">
            <ExpenseForm initialData={legacy.expense} projects={legacy.projects} employeeId={employeeId}
              onSubmit={saveLegacy} onCancel={() => setLegacy(null)} apiBaseUrl={apiService.getBaseUrl()} />
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceDrawer;
